import { useCallback, useEffect, useRef } from 'react';
import { useAudioRecorder } from './useAudioRecorder';
import { useTtsPlayer } from './useTtsPlayer';
import type { IPendingAction } from '../store/useChatStore';
import { useChatStore } from '../store/useChatStore';
import { useToast } from './useToast';

const DEBOUNCE_MS = 100;
const PROCESSING_TIMEOUT_MS = 20_000;

/** Check if the AI response ends with a question (ignoring trailing whitespace/punctuation noise) */
function endsWithQuestion(text: string): boolean {
  return /\?\s*$/.test(text.trim());
}

export function useVoiceMode() {
  const { start: startRecording, stop: stopRecording, cancel: cancelRecording, error: recorderError, analyserRef: recorderAnalyserRef } = useAudioRecorder();
  const ttsPlayer = useTtsPlayer();
  const sendVoiceMessage = useChatStore((s) => s.sendVoiceMessage);
  const voiceState = useChatStore((s) => s.voiceState);
  const setVoiceState = useChatStore((s) => s.setVoiceState);
  const setChatMode = useChatStore((s) => s.setChatMode);
  const lastVoiceResponse = useChatStore((s) => s.lastVoiceResponse);
  const showToast = useToast((s) => s.showToast);
  const lastToggleRef = useRef(0);
  const prevVoiceStateRef = useRef(voiceState);
  const processingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const shouldBeginRecording = useChatStore((s) => s.shouldBeginRecording);
  const pendingContinuation = useChatStore((s) => s.pendingContinuation);

  const beginRecording = useCallback(async () => {
    try {
      await startRecording();
      setVoiceState('listening');
    } catch {
      showToast('Microphone access denied. Switching to text mode.', 'danger');
      setChatMode('text');
      setVoiceState('idle');
    }
  }, [startRecording, setVoiceState, setChatMode, showToast]);

  /** Start recording without changing voice state (used for auto-record during approval) */
  const beginSilentRecording = useCallback(async () => {
    try {
      await startRecording();
    } catch {
      // Mic denied during approval — not critical, user can still tap buttons
    }
  }, [startRecording]);

  // When VoiceOverlay's "Suggest changes" dismisses (→ idle) and sets the flag,
  // this effect fires and starts recording so the user stays in voice mode.
  useEffect(() => {
    if (shouldBeginRecording && voiceState === 'idle') {
      useChatStore.setState({ shouldBeginRecording: false });
      beginRecording();
    }
  }, [shouldBeginRecording, voiceState, beginRecording]);

  // Cleanup: when leaving awaiting_approval for a non-recording state, cancel the auto-recording
  useEffect(() => {
    const prev = prevVoiceStateRef.current;
    prevVoiceStateRef.current = voiceState;

    if (prev === 'awaiting_approval' && voiceState !== 'awaiting_approval' && voiceState !== 'listening') {
      cancelRecording();
    }
  }, [voiceState, cancelRecording]);

  // Processing timeout — if processing exceeds 20s, bail to idle
  useEffect(() => {
    if (voiceState === 'processing') {
      processingTimerRef.current = setTimeout(() => {
        if (useChatStore.getState().voiceState === 'processing') {
          useChatStore.setState({ voiceState: 'idle', lastVoiceResponse: null, pendingContinuation: null });
          showToast('Something went wrong — try again', 'danger');
        }
      }, PROCESSING_TIMEOUT_MS);
    } else {
      if (processingTimerRef.current) {
        clearTimeout(processingTimerRef.current);
        processingTimerRef.current = null;
      }
    }

    return () => {
      if (processingTimerRef.current) {
        clearTimeout(processingTimerRef.current);
        processingTimerRef.current = null;
      }
    };
  }, [voiceState, showToast]);

  /** Speak a response via TTS and transition to the appropriate next state */
  const speakAndTransition = useCallback(async (
    response: { assistantText: string; pendingActions?: IPendingAction[]; messageId: string }
  ) => {
    // Bail if voice was cancelled (e.g. escape pressed) while we were waiting
    if (useChatStore.getState().voiceState === 'idle') return;

    const transitionToApproval = async () => {
      useChatStore.setState({
        lastVoiceResponse: {
          userText: '',
          assistantText: response.assistantText,
          pendingActions: response.pendingActions,
          messageId: response.messageId,
        },
      });
      setVoiceState('awaiting_approval');
      // Auto-start recording for revisions (mic goes live when overlay appears)
      await beginSilentRecording();
    };

    try {
      const result = await ttsPlayer.speak(
        response.assistantText,
        () => setVoiceState('speaking'),
      );

      if (result === 'interrupted') return;

      // TTS completed naturally — decide next state
      const hasPendingActions = response.pendingActions?.some(a => a.status === 'pending');

      if (hasPendingActions) {
        await transitionToApproval();
      } else if (endsWithQuestion(response.assistantText)) {
        useChatStore.setState({ lastVoiceResponse: null });
        await beginRecording();
      } else {
        setVoiceState('idle');
        useChatStore.setState({ lastVoiceResponse: null });
      }
    } catch {
      // TTS failed — skip speaking, proceed to post-TTS decision logic
      const hasPendingActions = response.pendingActions?.some(a => a.status === 'pending');

      if (hasPendingActions) {
        await transitionToApproval();
      } else {
        setVoiceState('idle');
        useChatStore.setState({ lastVoiceResponse: null });
      }
    }
  }, [ttsPlayer, setVoiceState, beginRecording, beginSilentRecording]);

  const finishRecordingAndProcess = useCallback(async () => {
    try {
      const audioBlob = await stopRecording();
      // Pre-warm AudioContext + worklet while we wait for transcription + chat API
      ttsPlayer.warmup();
      // sendVoiceMessage sets voiceState to 'processing' internally
      await sendVoiceMessage(audioBlob);

      // After sendVoiceMessage completes, check the store for result
      const response = useChatStore.getState().lastVoiceResponse;
      if (!response) return;

      await speakAndTransition(response);
    } catch (err) {
      setVoiceState('idle');
      const message = err instanceof Error ? err.message : 'Recording failed';
      showToast(message, 'danger');
    }
  }, [stopRecording, sendVoiceMessage, setVoiceState, ttsPlayer, showToast, speakAndTransition]);

  /** Stop approval recording and send it as a revision (reject + send voice message) */
  const sendApprovalRevision = useCallback(async () => {
    try {
      const audioBlob = await stopRecording();
      // Reject pending actions with context so cancelled_proposals are included
      const current = useChatStore.getState().lastVoiceResponse;
      if (current?.messageId) {
        useChatStore.getState().rejectActionsWithContext(current.messageId);
      }
      useChatStore.setState({ lastVoiceResponse: null });

      ttsPlayer.warmup();
      await sendVoiceMessage(audioBlob);

      const response = useChatStore.getState().lastVoiceResponse;
      if (!response) return;

      await speakAndTransition(response);
    } catch (err) {
      setVoiceState('idle');
      const message = err instanceof Error ? err.message : 'Recording failed';
      showToast(message, 'danger');
    }
  }, [stopRecording, sendVoiceMessage, setVoiceState, ttsPlayer, showToast, speakAndTransition]);

  // Bridge from confirmActions → TTS: when a continuation arrives while processing, speak it
  useEffect(() => {
    if (pendingContinuation && voiceState === 'processing') {
      useChatStore.setState({ pendingContinuation: null });
      speakAndTransition(pendingContinuation);
    }
  }, [pendingContinuation, voiceState, speakAndTransition]);

  const toggleVoice = useCallback(async () => {
    // Debounce rapid taps
    const now = Date.now();
    if (now - lastToggleRef.current < DEBOUNCE_MS) return;
    lastToggleRef.current = now;

    switch (voiceState) {
      case 'idle':
        await beginRecording();
        break;

      case 'listening':
        await finishRecordingAndProcess();
        break;

      case 'speaking':
        // INTERRUPT: stop TTS, start new recording
        ttsPlayer.stop();
        await beginRecording();
        break;

      case 'awaiting_approval':
        // User tapped FAB (checkmark) — send recording as a revision
        await sendApprovalRevision();
        break;

      case 'processing':
        // Can't interrupt processing — ignore
        break;
    }
  }, [voiceState, beginRecording, finishRecordingAndProcess, sendApprovalRevision, ttsPlayer]);

  /** Escape hatch — cancel everything and return to idle */
  const cancelVoice = useCallback(() => {
    switch (voiceState) {
      case 'listening':
        // Discard the recording without sending
        cancelRecording();
        setVoiceState('idle');
        break;
      case 'speaking':
        ttsPlayer.stop();
        setVoiceState('idle');
        useChatStore.setState({ lastVoiceResponse: null });
        break;
      case 'awaiting_approval':
        // Cancel auto-recording and dismiss overlay
        cancelRecording();
        useChatStore.getState().dismissVoiceOverlay();
        break;
      case 'processing':
        // Can't cancel mid-request, but user intent is clear — go idle when it finishes
        setVoiceState('idle');
        useChatStore.setState({ lastVoiceResponse: null, pendingContinuation: null });
        break;
      case 'idle':
        // Already idle — nothing to do
        break;
    }
  }, [voiceState, cancelRecording, ttsPlayer, setVoiceState]);

  return {
    voiceState,
    recorderError,
    toggleVoice,
    cancelVoice,
    ttsPlayer,
    lastVoiceResponse,
    recorderAnalyserRef,
  };
}
